import { useMemo } from 'react';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AttachmentsCard } from '@/components/attachments/AttachmentsCard';
import { MediaCapture, type MediaItem } from '@/components/inspection/MediaCapture';
import {
  uploadFile,
  useDeleteAttachment,
  useMultiEntityAttachments,
  useRegisterAttachment,
  useRequestUploadUrl,
  type EntityRef,
} from '@/hooks/useAttachments';

interface CourtesyLoanPhotosProps {
  loanId: string;
}

/**
 * Photos on a courtesy loan. Portal `AttachmentEntityType` has not grown
 * `courtesy_loan` yet; the staff-api already accepts that entity type.
 */
export function CourtesyLoanPhotos({ loanId }: CourtesyLoanPhotosProps) {
  const { t } = useTranslation('workorders');
  const entityLinks = useMemo(
    (): EntityRef[] => [
      { entityType: 'courtesy_loan', entityId: loanId },
    ],
    [loanId],
  );
  const { data: attachments = [] } = useMultiEntityAttachments(entityLinks);
  const requestUploadUrl = useRequestUploadUrl();
  const registerAttachment = useRegisterAttachment();
  const deleteAttachment = useDeleteAttachment();

  const media: MediaItem[] = attachments
    .filter((item) => item.mimeType.startsWith('image/'))
    .map((item) => {
      const entry: MediaItem = {
        id: item.id,
        type: 'photo',
        url: item.fileUrl,
      };
      if (item.description) entry.caption = item.description;
      return entry;
    });

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('courtesy.photos')}</Typography>
      <MediaCapture
        media={media}
        allowVideo={false}
        itemName={t('courtesy.photos')}
        onCapture={async (file) => {
          await uploadFile(
            file,
            entityLinks,
            ['courtesy_loan'],
            '',
            requestUploadUrl.mutateAsync,
            registerAttachment.mutateAsync,
          );
        }}
        onDelete={async (mediaId) => {
          await deleteAttachment.mutateAsync(mediaId);
        }}
      />
      <AttachmentsCard
        entityLinks={entityLinks}
        cardVariant="outlined"
        defaultTags={['courtesy_loan']}
      />
    </Stack>
  );
}
